import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import { ResponsiveLine, LineCustomSvgLayer, LineSeries } from "@nivo/line";
import dayjs from "dayjs";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import type { InsiderTradingData } from "../hooks/useInsiderTrading";
import { getChartColors } from "../constants/chartColors";
import { evenlySpacedTickValues } from "../utils/evenlySpacedTickValues";
import ChartTooltip from "./shared/ChartTooltip";
import ChartTooltipPortal from "./shared/ChartTooltipPortal";
import {
  compactShareCount,
  formatShareCount,
  getNotableInsiderBars,
  getInsiderBarPositions,
  INSIDER_BAR_WIDTH,
  InsiderActivity,
} from "./insiderActivityData";

interface InsiderActivityChartProps {
  activity: InsiderActivity;
  from: string;
  to: string;
  isLoading: boolean;
  onSelectTrade: (trade: InsiderTradingData) => void;
}

export const InsiderActivityChart: React.FC<InsiderActivityChartProps> = ({
  activity,
  from,
  to,
  isLoading,
  onSelectTrade,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { formatPrice } = useStockCurrency();
  const containerRef = useRef<HTMLDivElement>(null);
  const { months, pricePoints, tradeBars } = activity;
  const [hoveredBar, setHoveredBar] = useState<
    InsiderActivity["tradeBars"][number] | null
  >(null);
  const [chartWidth, setChartWidth] = useState(0);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setChartWidth(entry.contentRect.width);
        setHoveredBar(null);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isLoading]);
  const notableBars = useMemo(
    () =>
      getNotableInsiderBars(
        tradeBars,
        dayjs(from).startOf("day").valueOf(),
        dayjs(to).endOf("day").valueOf(),
        Math.max(0, chartWidth - 132),
      ),
    [tradeBars, from, to, chartWidth],
  );
  const hasPrices = pricePoints.length > 0;
  const priceColor = getChartColors(theme.palette.mode === "dark").positive;
  const buyColor =
    theme.palette.mode === "light"
      ? darken(theme.palette.success.dark, 0.2)
      : theme.palette.success.main;
  const saleColor =
    theme.palette.mode === "light"
      ? theme.palette.error.dark
      : theme.palette.error.main;
  const hoveredLabel =
    hoveredBar?.kind === "sale" ? "Total shares sold" : "Total shares bought";
  const hoveredColor = hoveredBar?.kind === "sale" ? saleColor : buyColor;
  const barOpacity = theme.palette.mode === "light" ? 0.75 : 0.55;
  const largestVolume = Math.max(4, ...notableBars.map((bar) => bar.shares));
  const volumeStep = 10 ** Math.floor(Math.log10(largestVolume));
  const volumeMax = Math.ceil(largestVolume / volumeStep) * volumeStep;
  const lineColors = [hasPrices ? priceColor : "transparent"];
  const priceAxis = hasPrices
    ? {
        tickSize: 0,
        tickPadding: 12,
        tickValues: 5,
        format: (value: number | string) => formatPrice(Number(value)),
      }
    : null;
  const chartData = useMemo(
    () => [
      {
        id: "Closing price",
        data: hasPrices
          ? pricePoints
          : months.map((month) => ({ x: month.x, y: 0 })),
      },
    ],
    [hasPrices, pricePoints, months],
  );
  // Evenly spaced over the trading days actually drawn, so the ends are
  // labelled and no two labels can meet (month starts put a partial first
  // month's label a few days from the next one).
  const ticks = evenlySpacedTickValues(
    hasPrices ? pricePoints.map((point) => point.x) : months.map((month) => month.x),
    isMobile,
  );
  // Past six months the labels span two years, so they carry the year.
  const tickFormat = dayjs(to).diff(dayjs(from), "month") > 6 ? "MMM ’YY" : "MMM D";

  const renderBars = useCallback(
    (
      { xScale, innerHeight }: Parameters<LineCustomSvgLayer<LineSeries>>[0],
      interactive: boolean,
    ) => {
      const width = INSIDER_BAR_WIDTH;
      const positions = getInsiderBarPositions(
        notableBars.map((bar) => ({ index: bar.index, x: xScale(bar.x) })),
      );
      return notableBars.map((bar) => {
        const height = Math.max(1, (bar.shares / volumeMax) * innerHeight);
        const hitHeight = Math.max(6, height);
        const y = innerHeight - height;
        const hitY = innerHeight - hitHeight;
        const x = (positions.get(bar.index) ?? xScale(bar.x)) - width / 2;
        const color = bar.kind === "purchase" ? buyColor : saleColor;
        const action = bar.kind === "sale" ? "sold" : "bought";
        const label = `${dayjs(bar.x).format("MMM D, YYYY")}: ${formatShareCount(bar.shares)} shares ${action} across ${bar.count} transactions. Show largest transaction`;
        if (!interactive)
          return (
            <rect
              key={bar.index}
              x={x}
              y={y}
              width={width}
              height={height}
              fill={color}
              fillOpacity={hoveredBar?.index === bar.index ? 1 : barOpacity}
              stroke={theme.palette.background.paper}
              strokeWidth={0.5}
              rx={1}
            />
          );
        return (
          <rect
            key={bar.index}
            x={x}
            y={hitY}
            width={width}
            height={hitHeight}
            fill="transparent"
            role="button"
            tabIndex={0}
            aria-label={label}
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setHoveredBar(bar)}
            onMouseLeave={() => setHoveredBar(null)}
            onClick={(event) => {
              event.stopPropagation();
              setHoveredBar(null);
              onSelectTrade(bar.trade);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setHoveredBar(null);
                onSelectTrade(bar.trade);
              }
            }}
          />
        );
      });
    },
    [
      notableBars,
      volumeMax,
      hoveredBar,
      barOpacity,
      buyColor,
      saleColor,
      theme,
      onSelectTrade,
    ],
  );
  // nivo mounts a custom layer as a component, so a new function identity per
  // render would remount every bar; memoise on what the layers actually read.
  const VolumeLayer = useCallback<LineCustomSvgLayer<LineSeries>>(
    (props) => (
      <g aria-label="Insider transaction share volume">
        <text
          x={props.innerWidth + 12}
          y={-12}
          fill={theme.palette.text.secondary}
          fontSize={10}
        >
          Shares
        </text>
        <line
          x1={0}
          x2={props.innerWidth}
          y1={props.innerHeight}
          y2={props.innerHeight}
          stroke={theme.palette.divider}
        />
        {renderBars(props, false)}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
          <text
            key={fraction}
            x={props.innerWidth + 12}
            y={props.innerHeight * (1 - fraction)}
            dominantBaseline="middle"
            fill={theme.palette.text.secondary}
            fontSize={11}
          >
            {compactShareCount(volumeMax * fraction)}
          </text>
        ))}
      </g>
    ),
    [renderBars, volumeMax, theme],
  );
  const TradeHitLayer = useCallback<LineCustomSvgLayer<LineSeries>>(
    (props) => <g>{renderBars(props, true)}</g>,
    [renderBars],
  );

  if (isLoading)
    return (
      <Skeleton
        aria-label="Loading insider activity chart"
        variant="rounded"
        height={320}
        sx={{ bgcolor: "action.hover" }}
      />
    );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          columnGap: 2,
          rowGap: 0.5,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            alignItems: "center",
          }}
        >
          {[
            { label: "Closing price", color: priceColor, line: true },
            { label: "Buys", color: buyColor, line: false },
            { label: "Sells", color: saleColor, line: false },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 6,
                  bgcolor: item.color,
                  borderRadius: 0.25,
                  ...(item.line && { height: 2 }),
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        ref={containerRef}
        sx={{
          minWidth: 0,
          userSelect: "none",
          "& rect[role=button]:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box sx={{ height: { xs: 300, md: 340 } }}>
          <ResponsiveLine
            data={chartData}
            margin={{ top: 24, right: 60, bottom: 38, left: 72 }}
            xScale={{
              type: "linear",
              min: dayjs(from).startOf("day").valueOf(),
              max: dayjs(to).endOf("day").valueOf(),
              // Nivo rounds a linear domain out to "nice" numbers by default,
              // which on millisecond timestamps adds weeks either side and
              // leaves the line short of both edges.
              nice: false,
            }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
              stacked: false,
            }}
            colors={lineColors}
            curve="linear"
            lineWidth={2}
            enablePoints={pricePoints.length === 1}
            pointSize={5}
            enableGridX={false}
            gridYValues={5}
            axisLeft={priceAxis}
            axisBottom={{
              tickSize: 0,
              tickPadding: 12,
              tickValues: ticks,
              format: (value) => dayjs(Number(value)).format(tickFormat),
            }}
            layers={[
              "grid",
              "axes",
              VolumeLayer,
              "lines",
              "points",
              TradeHitLayer,
            ]}
            isInteractive={false}
            enableSlices={false}
            enableCrosshair={false}
            animate={false}
            role="group"
            ariaLabel="Closing price with notable insider share volume. Purchases and sales rise from the horizontal axis, with colors indicating transaction type. Select a bar to see its largest transaction in the table."
            theme={{
              text: {
                fontFamily: theme.typography.fontFamily,
                fill: theme.palette.text.secondary,
              },
              axis: {
                ticks: {
                  text: { fill: theme.palette.text.secondary, fontSize: 11 },
                },
                domain: { line: { stroke: "transparent" } },
              },
              grid: {
                line: {
                  stroke: alpha(theme.palette.text.primary, 0.06),
                  strokeWidth: 1,
                },
              },
            }}
          />
        </Box>
      </Box>
      {hoveredBar && (
        <ChartTooltipPortal containerRef={containerRef}>
          <ChartTooltip
            header={dayjs(hoveredBar.x).format("MMM D, YYYY")}
            rows={[
              {
                label: hoveredLabel,
                value: formatShareCount(hoveredBar.shares),
                color: hoveredColor,
              },
              {
                label: "Transactions",
                value: formatShareCount(hoveredBar.count),
              },
            ]}
          />
        </ChartTooltipPortal>
      )}
    </Box>
  );
};
